import React, { useContext } from 'react';
import get from 'lodash/get';

import { ActionsSpecs } from 'src/presentation/components/pages/sidekick/threads/SidekickActionsConstants';
import AddTopicsControl from './Components/controls/AddTopicsControl';
import AiRephraseControl from './Components/controls/AiRephraseControl';
import BoolControl from './Components/Bool';
import ButtonSet from './Components/ButtonSet';
import CommaSeparatedStringArray from './Components/controls/CommaSeparatedStringArray';
import { ControlSpec, ControlType } from 'src/models/Sidekick/SidekickModuleSpec';
import FilterBy from './Components/FilterBy';
import FilterField from './Components/FilterField';
import FormContext from 'src/presentation/contexts/FormContext';
import MemoryExplorer from './Components/MemoryExplorer/MemoryExplorer';
import MemoryNameInput from './Components/controls/MemoryNameInput';
import Number from './Components/Number';
import Rule from './Components/Rule/Rule';
import StringArray from './Components/StringArray';
import StringControl from './Components/controls/StringControl';
import TemplateInput from './Components/controls/TemplateInput';
import TemplateSelector from './Components/controls/TemplateSelector';
import { ThreadModuleContext } from './ThreadModuleContext';
import TimestampControl from './Components/controls/TimestampControl';
import TopicsMultiSelect from './Components/controls/TopicsMultiSelect';

type ControlProps = {
  controlSpec: ControlSpec;
  specAttr: string;
};

const controlTypeToComponentMap = {
  [ControlType.ADD_TOPICS]: props => <AddTopicsControl {...props} />,
  [ControlType.AI_REPHRASE]: props => <AiRephraseControl {...props} />,
  [ControlType.BOOL]: props => <BoolControl {...props} />,
  [ControlType.BUTTON_SET]: props => <ButtonSet {...props} />,
  [ControlType.COMMA_SEPARATED_STRING_ARRAY]: props => <CommaSeparatedStringArray {...props} />,
  [ControlType.FILTER_BY]: props => <FilterBy {...props} />,
  [ControlType.FILTER_FIELD]: props => <FilterField {...props} />,
  [ControlType.MEMORY_EXPLORER]: props => <MemoryExplorer {...props} />,
  [ControlType.MEMORY_EXPLORER_NAME]: props => <MemoryNameInput {...props} />,
  [ControlType.NUMBER]: props => <Number {...props} />,
  [ControlType.RULE]: props => <Rule {...props} />,
  [ControlType.STRING_ARRAY]: props => <StringArray {...props} />,
  [ControlType.STRING]: props => <StringControl {...props} />,
  [ControlType.TOPICS_MULTI_SELECT]: props => <TopicsMultiSelect {...props} />,
  [ControlType.TIMESTAMP]: props => <TimestampControl {...props} />,

  [ControlType.HIDDEN]: () => <></>,
  [ControlType.TEMPLATE]: props => <TemplateSelector {...props} />,
  [ControlType.TEMPLATE_INPUT]: props => <TemplateInput {...props} />,
};

export const controlTypeComponentFactory: (type: ControlType, props: unknown) => React.ReactElement = (
  type: ControlType,
  props: unknown
) => {
  return controlTypeToComponentMap[type](props);
};

const Control = ({ controlSpec, specAttr }: ControlProps) => {
  const { readOnly, isModifier, module, modifier } = useContext(ThreadModuleContext);
  const modifierOrModule = isModifier ? modifier : module;

  const { workingState } = useContext(FormContext);

  const spec = get(ActionsSpecs[modifierOrModule!.name], specAttr);

  let extendSpec = controlSpec as ControlSpec & { description?: string; isHtml?: boolean; placeholder?: string };

  if (spec) {
    extendSpec = { ...controlSpec, ...spec };

    if (spec.hidden || (spec.visibleIfAttr && !get(workingState, spec.visibleIfAttr, false))) {
      return null;
    }
  }

  const attr = `controlValues.${controlSpec.name}`;

  const renderControlComponent = controlTypeComponentFactory(extendSpec.type, {
    'data-aid': controlSpec.name,
    attr,
    description: extendSpec.description,
    disabled: readOnly,
    isHtml: extendSpec.isHtml,
    isReversed: ActionsSpecs[modifierOrModule!.name]?.isReversed,
    placeholder: extendSpec.placeholder,
    typeSpec: extendSpec,
    ...controlSpec,
    prompt: extendSpec.prompt,
  });

  return <>{renderControlComponent}</>;
};

export default Control;
